import React from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import {
  Box,
  ButtonBase,
  Collapse,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import chevronDown from "@iconify/icons-mdi/chevron-down";
import chevronRight from "@iconify/icons-mdi/chevron-right";
import { InlineIcon } from "@iconify/react";
import { OcSidebarChevron, OcSidebarSelector } from "oc/components";
import useStyles from "oc/components/sidebar-item/sidebar-item.style";
import { BadgeType, renderBadge } from "oc/components/sidebar/badge-registry";

interface OcSidebarCollapseProps {
  id: string;
  badge?: BadgeType;
  icon?: any;
  label?: string;
  translation?: string;
  children: Array<any>;
  activeMenuItem: string;
  onMenuItemChange: (id: string) => void;
  onMenuChange?: (id: string) => void;
  narrowMenu?: boolean;
  narrowMenuOpened?: boolean;
  onChangeTemporaryDrawer?: () => void;
}

export const OcSidebarCollapse: React.FC<OcSidebarCollapseProps> = ({
  id,
  badge,
  icon,
  label,
  translation,
  children,
  activeMenuItem,
  onMenuItemChange,
  onMenuChange,
  narrowMenu,
  narrowMenuOpened,
  onChangeTemporaryDrawer,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const handleClick = () => {
    if (activeMenuItem === id) {
      onMenuItemChange("");
    } else {
      onMenuItemChange(id);
    }
  };

  let menuLabel = t(translation) || label;

  const { pathname } = useLocation();

  let isActiveRoute = children?.find((item) => item.path === pathname);

  let openCollapse = isActiveRoute || activeMenuItem === id ? true : false;

  return (
    <Box id={id} className={classes.buttonBase}>
      <ButtonBase
        sx={{
          width: "100%",
        }}
      >
        <ListItem
          id={id}
          component="div"
          style={{
            color: "white",
            cursor: "pointer",
            backgroundColor:
              openCollapse && isActiveRoute ? "#3E4345" : "unset",
          }}
          onClick={handleClick}
        >
          {icon && (
            <Box minWidth="24px" maxWidth="24px">
              <InlineIcon icon={icon} width="24px" />
            </Box>
          )}
          <ListItemText
            sx={{
              padding: "2px 16px",
            }}
          >
            <Typography variant="body1" color="#fff">
              {menuLabel}
            </Typography>
          </ListItemText>
          {badge && <Box marginRight="16px">{renderBadge(badge)}</Box>}
          {openCollapse ? (
            <OcSidebarChevron icon={chevronDown} />
          ) : (
            <OcSidebarChevron icon={chevronRight} />
          )}
        </ListItem>
      </ButtonBase>
      <Collapse in={openCollapse} timeout="auto" unmountOnExit>
        <List
          component="div"
          disablePadding
          style={{
            backgroundColor:
              openCollapse && isActiveRoute ? "#3E4345" : "unset",
          }}
        >
          {children.map((item, index) => {
            return (
              <OcSidebarSelector
                key={`${index}_${item.id}`}
                item={item}
                onMenuChange={onMenuChange ?? (() => {})}
                onMenuItemChange={onMenuItemChange}
                activeMenuItem={activeMenuItem}
                narrowMenu={narrowMenu ?? false}
                narrowMenuOpened={narrowMenuOpened ?? false}
                onChangeTemporaryDrawer={onChangeTemporaryDrawer}
              />
            );
          })}
        </List>
      </Collapse>
    </Box>
  );
};

export default OcSidebarCollapse;
